import React, { useState, useEffect, ChangeEvent } from 'react';
import { toastr } from 'react-redux-toastr';
import i18next from 'i18next';
import { Box, Grid, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

import Logo from '../Common/Logo/Logo';
import auth from '@/apis/auth';
import { errorConfig } from '@/config/i18n';
import { NewTextField } from '../Common/Fields';
import { makeStyles } from '@mui/styles';
import { frontendurl } from '../../index';
import { getPathKey } from '@/common/common';
import { PasswordRequirements } from './PasswordRequirements';

// Define the types for state
interface DataState {
  password: string;
  confirmPassword: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'flex-start',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'center',
      padding: '48px 0',
    },
  },
  appContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: '0 auto',
    boxShadow: 'none',
    [theme.breakpoints.up('lg')]: {
      width: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      margin: '0 20px',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      justifyContent: 'unset',
      width: '100%',
      padding: '10px',
      margin: '0 5px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      margin: '0 5px',
      padding: '10px',
      justifyContent: 'unset',
    },
  },
  cardLayout: {
    width: '352px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    padding: '40px 24px',
    borderRadius: '8px',
    margin: '0 auto',
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.up('lg')]: {
      width: '100%',
      maxWidth: '416px',
      padding: '40px 30px',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '100%',
      boxShadow: 'none',
      borderRadius: '0',
      maxWidth: '416px',
      padding: '50px 32px',
    },
    [theme.breakpoints.down('sm')]: {
      boxShadow: 'none',
    },
  },
  formSignin: {
    margin: '0 auto',
  },
  ButtonStyle: {
    height: '50px',
    width: '100%',
    margin: theme.spacing(3, 0, 0, 0),
    backgroundColor: theme.palette.primary.main,
    borderRadius: '4px',
    [theme.breakpoints.up('lg')]: {
      height: '50px',
      fontSize: '16px',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      height: '50px',
      fontSize: '14px',
      margin: theme.spacing(2, 0, 0, 0),
    },
    [theme.breakpoints.down('sm')]: {
      height: '45px',
      fontSize: '12px',
    },
  },
}));

const ActivateUser: React.FC = () => {
  const classes = useStyles();
  const [key, setKey] = useState<string | null>(null);
  const [passwordMatches, setPasswordMatches] = useState<boolean>(false);
  const [data, setData] = useState<DataState>({
    password: '',
    confirmPassword: '',
  });
  const { password, confirmPassword } = data;
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  useEffect(() => {
    setKey(getPathKey());
  }, []);

  const handleChange = ({ target: { value, name } }: ChangeEvent<HTMLInputElement>) => {
    const newData = { ...data };
    newData[name as keyof DataState] = value;
    setPasswordMatches(newData.password === newData.confirmPassword);
    setData(newData);
  };

  const send = () => {
    const requestData = {
      password: password,
      confirm_password: confirmPassword,
    };

    const requirementsMet =
      password.length >= 8 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /\d/.test(password) &&
      /[@$!%*?&*\- _+={}\[\]|:;"'<>,.?/~`]/.test(password);

    if (!password || password.length === 0) {
      toastr.error(i18next.t('common:login.password'), i18next.t('common:login.providePass'), errorConfig);
      return;
    }

    if (!requirementsMet) {
      toastr.error(i18next.t('common:login.password'), i18next.t('common:login.pwdResetValidationError'), errorConfig);
      return;
    }

    if (passwordMatches) {
      const pass = JSON.stringify(requestData);

      if (pass) {
        auth
          .activateUser(pass, key)
          .then(() => {
            toastr.success(i18next.t('common:login.activateUser'), i18next.t('common:login.userActivatedSuccessfully'));
            window.location.assign(frontendurl);
          })
          .catch((error) => {
            if (error.response) {
              const status = error.response.status;
              switch (status) {
                case 400:
                  toastr.error(i18next.t('common:error'), i18next.t('common:login.activateUserBadRequest'), errorConfig);
                  break;
                case 404:
                  toastr.error(i18next.t('common:error'), i18next.t('common:login.activateUserEmailNotFoundError'), errorConfig);
                  break;
                case 422:
                  toastr.error(i18next.t('common:error'), i18next.t('common:login.activateUserValidationError'), errorConfig);
                  break;
                default:
                  toastr.error(i18next.t('common:error'), i18next.t('common:login.generalError'), errorConfig);
                  break;
              }
            } else {
              toastr.error(i18next.t('common:error'), i18next.t('common:login.generalError'), errorConfig);
            }
          });
      }
    } else {
      toastr.error(i18next.t('common:login.password'), i18next.t('common:login.uneven'), errorConfig);
    }
  };

  return (
    <Box className={classes.root}>
      <Paper className={classes.appContent}>
        <Grid className={classes.cardLayout}>
          <Grid className="Logo">
            <Logo w="300" h="50" />
          </Grid>
          <form className="form-signin" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <Grid>
              <NewTextField
                onChange={handleChange}
                value={password}
                name="password"
                type={showPassword ? 'text' : 'password'}
                data-cy="password"
                label={i18next.t('common:login:password')}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword((prevState) => !prevState)} edge="end">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  ),
                }}
              />
              <PasswordRequirements password={password} />
            </Grid>
            <Grid>
              <NewTextField
                onChange={handleChange}
                value={confirmPassword || ''}
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                data-cy="confirmPassword"
                label={i18next.t('common:login.confirmPassword')}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowConfirmPassword((prevState) => !prevState)} edge="end">
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid>
              <Button
                className={classes.ButtonStyle}
                data-cy="submit"
                onClick={send}
                type="button"
                variant="contained"
                color="primary"
              >
                <Typography variant="button">{i18next.t('common:login.submit')}</Typography>
              </Button>
            </Grid>
          </form>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ActivateUser;
